import React from "react";
import { ProductTitle, fontFamilies } from "../typography";
import { Spacing, Column, Row } from "../helpers/layout";
import { Product } from "../types/Product";
import { css } from "styled-components";
import { EditorContent } from "./EditorContent";
import { getImageUrl } from "../helpers/getImageUrl";
import { AddToWishlistButton } from "./AddToWishlistButton";
import { CompareButton } from "./CompareButton";
import { Link } from "./Link";
import AwardIcon from "../icons/AwardIcon";
import Router from "next/router";
import { Colors } from "../branding";
import { Button } from "./Button";
import BestsellingIcon from "../icons/BestsellingIcon";
import InMuseumIcon from "../icons/InMuseumIcon";
import { useCommonData } from "../context/CommonDataContext";

type ProductMini = Pick<
  Product,
  | "id"
  | "featured_image"
  | "name"
  | "slug"
  | "orientation"
  | "serie_description"
  | "sizes"
  | "awards_count"
  | "bestselling"
  | "in_museum"
  | "book_options"
> & {
  serie: {
    name: string;
  } | null;
};

function ProductThumb(props: { product: ProductMini }) {
  const { product } = props;
  const size = product.sizes[0];
  const image = product.featured_image;
  const { settings } = useCommonData();

  if (!size) return null;

  return (
    <div
      css={css`
        position: relative;
        width: 100%;
        height: 0;
        padding-bottom: ${(size.height / size.width) * 100}%;
        background: ${image
          ? `url(${getImageUrl(image, {
              width: "large",
            })})`
          : "none"};
        background-size: 100% 100%;
        background-position: center;
        overflow: hidden;
        cursor: pointer;

        .bg-overlay {
          transition: opacity 300ms;
          opacity: 0;
        }

        .overlay {
          bottom: -80px;
          transition: bottom 300ms;
        }

        &:hover {
          .bg-overlay {
            opacity: 0.3;
          }
          .overlay {
            bottom: 0;
          }
        }
      `}
      onClick={() => {
        Router.push("/shop/[slug]", `/shop/${product.slug}`);
      }}
    >
      <div
        className="bg-overlay"
        css={css`
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          background: ${Colors.white};
        `}
      ></div>
      <div
        className="overlay"
        css={css`
          position: absolute;
          padding: ${Spacing.m};
          left: 0;
          bottom: 0;
          width: 100%;
          box-sizing: border-box;
          background: rgba(255, 255, 255, 0.7);
          cursor: default;
        `}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Row justify="space-between" align="center">
          <ProductTitle>
            <Row align="center">
              <Link href={`/shop/${product.slug}`}>{product.name}</Link>

              {product.in_museum ? (
                <InMuseumIcon />
              ) : product.awards_count > 0 ? (
                <Row
                  gutter={Spacing.s}
                  align="center"
                  css={css`
                    margin-left: ${Spacing.m};
                  `}
                >
                  <AwardIcon />
                  <div
                    css={css`
                      font-family: ${fontFamilies.gotham};
                      font-size: 14px;
                      line-height: 1;
                      height: 14px;
                      font-weight: 400;
                    `}
                  >
                    {product.awards_count}
                  </div>
                </Row>
              ) : product.bestselling && !settings.gallery_focus ? (
                <BestsellingIcon />
              ) : null}
            </Row>
          </ProductTitle>

          <div
            css={css`
              flex: 0 0 auto;
            `}
          >
            <Row align="center">
              {!settings.gallery_focus ? (
                <>
                  <AddToWishlistButton
                    productId={product.id}
                    bookOptions={product.book_options}
                  />
                  <CompareButton
                    productId={product.id}
                    bookOptions={product.book_options}
                  />
                </>
              ) : null}
              <div
                css={css`
                  margin-left: ${Spacing.xl};
                `}
              >
                <Button
                  type="button"
                  label="View pricing"
                  style="black-border"
                  onClick={() => {
                    Router.push("/shop/[slug]", `/shop/${product.slug}`);
                  }}
                />
              </div>
            </Row>
          </div>
        </Row>
      </div>
    </div>
  );
}

export function ProductDescription(props: {
  product: {
    serie_description: any;
  };
}) {
  const { product } = props;

  return <EditorContent text={product.serie_description} />;
}

export function ProductBigThumb(props: {
  product: ProductMini;
  margin?: number;
}) {
  const { product } = props;
  const isHorizontal = product.orientation === "horizontal";
  const isVertical = product.orientation === "vertical";
  const hasDescription = !!product.serie_description;
  const margin = props.margin || 2;

  const marginBottom = css`
    margin-bottom: ${margin}%;
  `;

  if (isHorizontal) {
    return (
      <article className="grid-item grid-item--fullwidth" css={marginBottom}>
        <Column>
          <ProductThumb product={product} />
          {hasDescription && <ProductDescription product={product} />}
        </Column>
      </article>
    );
  }

  if (isVertical) {
    if (hasDescription) {
      return (
        <article className="grid-item grid-item--fullwidth" css={marginBottom}>
          <Row align="center" gutter={Spacing.xxxl}>
            <div
              css={css`
                flex: 0 0 48%;
              `}
            >
              <ProductThumb product={product} />
            </div>
            <div
              css={css`
                flex: 1 0 20%;
              `}
            >
              <ProductDescription product={product} />
            </div>
          </Row>
        </article>
      );
    } else {
      return (
        <article className="grid-item" css={marginBottom}>
          <ProductThumb product={product} />
        </article>
      );
    }
  }

  return null;
}
